import { easybackClient } from './api'
import type { BlogPost, BlogComment, BlogLike } from '@/types/index'

export const blogService = {
  getPosts: async (params?: { category?: string; limit?: number }) => {
    const result = await easybackClient.list<BlogPost>('blog_posts', {
      filter: params?.category ? { category: { eq: params.category } } : undefined,
      limit: params?.limit
    })
    return result.data || []
  },

  getPost: async (slug: string) => {
    const result = await easybackClient.list<BlogPost>('blog_posts', { filter: { slug: { eq: slug } } })
    return result.data?.[0]
  },

  getCategories: async () => {
    const result = await easybackClient.list('blog_categories')
    return result.data || []
  },

  getFeaturedPosts: async () => {
    const result = await easybackClient.list<BlogPost>('blog_posts', { filter: { is_featured: { eq: true } } })
    return result.data || []
  },

  getComments: async (postId: number) => {
    const result = await easybackClient.list<BlogComment>('blog_comments', {
      filter: {
        post_id: { eq: postId },
        is_approved: { eq: true },
        deleted_at: { is_null: true }
      },
      sort: { created_at: 'asc' }
    })
    const comments = result.data || []
    const rootComments = comments.filter(c => !c.parent_id)
    const repliesByParent = new Map<number, BlogComment[]>()
    comments.filter(c => c.parent_id).forEach(reply => {
      const parentId = reply.parent_id!
      if (!repliesByParent.has(parentId)) {
        repliesByParent.set(parentId, [])
      }
      repliesByParent.get(parentId)!.push(reply)
    })
    rootComments.forEach(comment => {
      comment.replies = repliesByParent.get(comment.id) || []
    })
    return rootComments
  },

  createComment: async (postId: number, content: string, parentId?: number) => {
    const userId = Number(localStorage.getItem('userId'))
    if (!userId) {
      throw new Error('Oturum açmanız gerekiyor')
    }
    const result = await easybackClient.insert<BlogComment>('blog_comments', {
      post_id: postId,
      user_id: userId,
      content,
      parent_id: parentId
    })
    return result.data?.[0]
  },

  deleteComment: async (commentId: number) => {
    await easybackClient.update('blog_comments', commentId, { deleted_at: new Date().toISOString() })
  },

  toggleLike: async (postId: number) => {
    const userId = Number(localStorage.getItem('userId'))
    if (!userId) {
      throw new Error('Oturum açmanız gerekiyor')
    }
    const existingResult = await easybackClient.list<BlogLike>('blog_likes', {
      filter: {
        post_id: { eq: postId },
        user_id: { eq: userId }
      }
    })
    const existing = existingResult.data?.[0]
    if (existing) {
      await easybackClient.delete('blog_likes', existing.id)
      return { liked: false, count: await blogService.getLikeCount(postId) }
    }
    await easybackClient.insert('blog_likes', {
      post_id: postId,
      user_id: userId
    })
    return { liked: true, count: await blogService.getLikeCount(postId) }
  },

  getLikeCount: async (postId: number) => {
    const result = await easybackClient.list<BlogLike>('blog_likes', {
      filter: { post_id: { eq: postId } }
    })
    return result.data?.length || 0
  },

  getUserLike: async (postId: number) => {
    const userId = Number(localStorage.getItem('userId'))
    if (!userId) return false
    const result = await easybackClient.list<BlogLike>('blog_likes', {
      filter: {
        post_id: { eq: postId },
        user_id: { eq: userId }
      }
    })
    return (result.data?.length || 0) > 0
  },

  incrementView: async (postId: number) => {
    const postResult = await easybackClient.get<BlogPost>('blog_posts', postId)
    const currentViews = postResult.data?.[0]?.view_count || 0
    await easybackClient.update('blog_posts', postId, { view_count: currentViews + 1 })
  }
}
